import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useWorkspace } from '../../context/WorkspaceContext';
import { testAssetApi } from '../../services/api';
import { Plus, Trash2, FileText, Image as ImageIcon, AlertCircle, CheckCircle2, MoreVertical, ExternalLink, Upload, X } from 'lucide-react';
import './TestAssetsSettings.css';

const CATEGORIES = [
    { id: 'profile_pic', label: 'Profile Picture' },
    { id: 'id_card', label: 'ID Card / Identity' },
    { id: 'receipt', label: 'Receipt / Invoice' },
    { id: 'document', label: 'General Document' },
    { id: 'other', label: 'Other' }
];

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB
const size_limit_human = `${MAX_FILE_SIZE / 1024 / 1024}MB`;

function TestAssetsSettings() {
    const { currentWorkspace } = useWorkspace();
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // New asset form state
    const [showAddForm, setShowAddForm] = useState(false);
    const [newName, setNewName] = useState('');
    const [newCategory, setNewCategory] = useState('other');
    const [selectedFile, setSelectedFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef(null);

    const fetchAssets = useCallback(async () => {
        if (!currentWorkspace?.id) return;
        setLoading(true);
        try {
            const response = await testAssetApi.list(currentWorkspace.id);
            setAssets(response.data.assets || []);
        } catch (err) {
            console.error('Failed to fetch test assets:', err);
            setError('Failed to load test assets');
        } finally {
            setLoading(false);
        }
    }, [currentWorkspace?.id]);

    useEffect(() => {
        fetchAssets();
    }, [fetchAssets]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > MAX_FILE_SIZE) {
            setError(`File size exceeds ${size_limit_human} limit`);
            setSelectedFile(null);
            e.target.value = '';
            return;
        }

        setError(null);
        setSelectedFile(file);
        if (!newName) {
            // Set default name from filename (without extension)
            setNewName(file.name.replace(/\.[^/.]+$/, ""));
        }
    };

    const handleCreateAsset = async (e) => {
        e.preventDefault();
        if (!newName || !selectedFile) {
            setError('Please provide a name and select a file');
            return;
        }

        setSubmitting(true);
        setError(null);
        setUploadProgress(0);

        try {
            // 1. Get pre-signed URL
            const { data: { uploadUrl, assetUrl } } = await testAssetApi.getUploadUrl({
                fileName: selectedFile.name,
                fileType: selectedFile.type,
                workspaceId: currentWorkspace.id
            });

            // 2. Upload to S3 directly
            await axios.put(uploadUrl, selectedFile, {
                headers: {
                    'Content-Type': selectedFile.type
                },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(progress);
                }
            });

            // 3. Register in backend
            await testAssetApi.create(currentWorkspace.id, {
                name: newName,
                category: newCategory,
                assetUrl: assetUrl,
                type: selectedFile.type,
                fileSize: selectedFile.size,
                metadata: {
                    originalName: selectedFile.name,
                    lastModified: selectedFile.lastModified
                }
            });

            setSuccessMessage(`${newName} added successfully`);
            setNewName('');
            setSelectedFile(null);
            setNewCategory('other');
            setShowAddForm(false);
            fetchAssets();

            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            console.error('Failed to create asset:', err);
            setError(err.response?.data?.error || 'Failed to upload asset');
        } finally {
            setSubmitting(false);
            setUploadProgress(0);
        }
    };

    const handleDeleteAsset = async (id) => {
        if (!window.confirm('Are you sure you want to delete this asset?')) return;

        try {
            await testAssetApi.delete(id);
            setAssets(assets.filter(a => a.id !== id));
            setSuccessMessage('Asset deleted');
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            console.error('Failed to delete asset:', err);
            setError('Failed to delete asset');
        }
    };

    const getCategoryLabel = (id) => {
        return CATEGORIES.find(c => c.id === id)?.label || id;
    };

    return (
        <div className="test-assets-settings">
            <div className="page-header">
                <div className="header-info">
                    <h1>Test Assets</h1>
                    <p>Manage shared media and files used across your workspace tests. These files are automatically available to the AI agent.</p>
                </div>
                {!showAddForm && (
                    <div className="header-actions">
                        <span className={`asset-count-indicator ${assets.length >= 5 ? 'at-limit' : ''}`}>
                            {assets.length} / 5 Assets
                        </span>
                        <button
                            className="btn btn-primary btn-with-icon"
                            onClick={() => setShowAddForm(true)}
                            disabled={assets.length >= 5}
                            title={assets.length >= 5 ? "Maximum limit of 5 assets reached" : "Add new asset"}
                        >
                            <Plus size={18} />
                            Add Asset
                        </button>
                    </div>
                )}
            </div>

            {assets.length >= 5 && !showAddForm && (
                <div className="alert-banner warning">
                    <AlertCircle size={20} />
                    <p>You have reached the maximum limit of 5 assets for this workspace. Delete an existing asset to upload a new one.</p>
                </div>
            )}

            {error && (
                <div className="alert-banner error">
                    <AlertCircle size={20} />
                    <p>{error}</p>
                </div>
            )}

            {successMessage && (
                <div className="alert-banner success">
                    <CheckCircle2 size={20} />
                    <p>{successMessage}</p>
                </div>
            )}

            {showAddForm && (
                <div className="section-card add-asset-card">
                    <div className="card-header">
                        <h2>Add New Test Asset</h2>
                    </div>
                    <form onSubmit={handleCreateAsset}>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Asset Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Standard ID Card"
                                    className="form-input"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Category</label>
                                <select
                                    className="form-input"
                                    value={newCategory}
                                    onChange={(e) => setNewCategory(e.target.value)}
                                >
                                    {CATEGORIES.map(c => (
                                        <option key={c.id} value={c.id}>{c.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>File Upload</label>
                            <div
                                className={`file-dropzone ${selectedFile ? 'has-file' : ''}`}
                                onClick={() => fileInputRef.current.click()}
                                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragging'); }}
                                onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('dragging'); }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    e.currentTarget.classList.remove('dragging');
                                    const file = e.dataTransfer.files[0];
                                    if (file) handleFileChange({ target: { files: [file] } });
                                }}
                            >
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    style={{ display: 'none' }}
                                    accept="image/*,application/pdf"
                                />
                                {selectedFile ? (
                                    <div className="selected-file-info">
                                        <CheckCircle2 size={32} className="text-success" />
                                        <div className="file-meta">
                                            <span className="file-name">{selectedFile.name}</span>
                                            <span className="file-size">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</span>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn-remove-file"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedFile(null);
                                                fileInputRef.current.value = '';
                                            }}
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="dropzone-content">
                                        <Upload size={32} />
                                        <p>Click or drag file to upload</p>
                                        <span className="upload-hint">Images or PDF (max {size_limit_human})</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {submitting && uploadProgress > 0 && (
                            <div className="upload-progress-container">
                                <div className="progress-bar-bg">
                                    <div className="progress-bar-fill" style={{ width: `${uploadProgress}%` }}></div>
                                </div>
                                <span className="progress-text">{uploadProgress}% uploaded</span>
                            </div>
                        )}

                        <div className="form-actions">
                            <button type="button" className="btn btn-secondary" onClick={() => setShowAddForm(false)}>Cancel</button>
                            <button type="submit" className="btn btn-primary" disabled={submitting || !selectedFile}>
                                {submitting ? 'Uploading...' : 'Upload & Add'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="assets-grid">
                {loading ? (
                    <div className="loading-placeholder">Loading assets...</div>
                ) : assets.length === 0 ? (
                    <div className="empty-state">
                        <FileText size={48} />
                        <h3>No test assets yet</h3>
                        <p>Add assets like ID cards, receipts, or avatars to use them in your AI tests.</p>
                        <button className="btn btn-outline" onClick={() => setShowAddForm(true)}>Add your first asset</button>
                    </div>
                ) : (
                    assets.map(asset => (
                        <div key={asset.id} className="asset-card">
                            <div className="asset-preview">
                                {asset.type.startsWith('image/') ? (
                                    <div className="preview-image" style={{ backgroundImage: `url(${asset.displayUrl || asset.assetUrl})` }}></div>
                                ) : (
                                    <div className="preview-icon">
                                        <FileText size={40} />
                                    </div>
                                )}
                                <div className="asset-category-badge">
                                    {getCategoryLabel(asset.category)}
                                </div>
                            </div>
                            <div className="asset-details">
                                <div className="asset-info">
                                    <h3>{asset.name}</h3>
                                    <div className="asset-meta-row">
                                        <span className="asset-type">{asset.type}</span>
                                        {asset.fileSize && (
                                            <span className="asset-size"> • {(asset.fileSize / 1024 / 1024).toFixed(2)} MB</span>
                                        )}
                                    </div>
                                </div>
                                <div className="asset-actions">
                                    <a href={asset.displayUrl || asset.assetUrl} target="_blank" rel="noopener noreferrer" className="btn-icon" title="View Original">
                                        <ExternalLink size={16} />
                                    </a>
                                    <button className="btn-icon text-danger" onClick={() => handleDeleteAsset(asset.id)} title="Delete">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

export default TestAssetsSettings;
